import { IRankerGroupConfiguration, IRankerSuggestionGroup, RankProps } from '@1js/search-types';
import { find, groupBy, mapValues, sum } from 'lodash-es';

interface SearchSuggestionStringMatchRankerProviderConfiguration {
    matchTitle: boolean;
    matchDescription: boolean;
    minLength: number;
    maxLength: number;
}

// Current providers that are allowed to be ranked to the top if there are exact string matches.
// Need to do some analysis and experiment on this.
const providerConfig: {
    [providerId: string]: SearchSuggestionStringMatchRankerProviderConfiguration;
} = {
    file: {
        matchTitle: true,
        matchDescription: true,
        minLength: 1,
        maxLength: 9
    },
    files: {
        matchTitle: true,
        matchDescription: true,
        minLength: 1,
        maxLength: 9
    },
    templatePreview: {
        matchTitle: true,
        matchDescription: false,
        minLength: 1,
        maxLength: 9
    },
    help: {
        matchTitle: true,
        matchDescription: true,
        minLength: 9,
        maxLength: Number.MAX_VALUE
    }
};

/**
 * @summary
 * This custom search suggestion provider ranker finds exact prefix or substring matches
 * in the search result titles and descriptions and ranks these providers on the top in
 * the search dropdown in addition to the provider order listed in the provider configuration.
 *
 * The calculation of number of results is the same as FAST default provider ranker. Providers
 * are allowed to show more than their optimal numbers if there are extra spaces given by the
 * providers which have less than their optimal numbers.
 *
 * @param _groupsConfig: search provider group configuration.
 * @param groups: search results.
 * @param _rankProps: extra props like search string.
 * @returns Array<IRankerSuggestionGroup>.
 */
export function searchSuggestionStringMatchProviderRanker(
    _groupsConfig: ReadonlyArray<IRankerGroupConfiguration>,
    groups: ReadonlyArray<IRankerSuggestionGroup>,
    _rankProps?: RankProps
  ): Array<IRankerSuggestionGroup> {
    const groupsConfigById = mapValues(groupBy(_groupsConfig, ({ id }) => id), array => array[0]);
    const searchTextNormalized: string = (_rankProps && _rankProps.searchText).toLowerCase() || '';
    const scores: {[name: string]: number} = {};
    const totalOptmalResults = sum(_groupsConfig.map(config => config.optimalCount));
    let totalResults = 0;

    /**
     * Finding exact prefix or substring matches in each search suggestion title and description,
     * and updating scores for these providers.
     */
    for (const group of groups) {
        scores[group.id] = 0;
        const optimalCount = groupsConfigById[group.id].optimalCount || Number.MAX_VALUE;
        totalResults += optimalCount;
        const groupConfig: SearchSuggestionStringMatchRankerProviderConfiguration = providerConfig[group.id];

        if (groupConfig && searchTextNormalized.length >= groupConfig.minLength && searchTextNormalized.length <= groupConfig.maxLength) {
            const maxCount = groupsConfigById[group.id].maxCount || Number.MAX_VALUE;
            const maxResult = Math.min(group.suggestions.length, maxCount, optimalCount);

            for (let i = 0; i < maxResult; i++) {
                const suggestion: any = group.suggestions[i];
                const matchTitle: string | undefined = (suggestion?.title || suggestion?.displayName || suggestion?.templatePreviewData?.Title)?.toLowerCase();
                const matchDescription: string | undefined = (suggestion?.secondaryDisplayText || suggestion?.templatePreviewData?.Description)?.toLowerCase();
                if (groupConfig.matchTitle && matchTitle) {
                    scores[group.id] += (matchTitle.indexOf(searchTextNormalized) !== -1 || searchTextNormalized.indexOf(matchTitle) !== -1) ?  1 : 0;
                } else if (groupConfig.matchDescription && matchDescription) {
                    scores[group.id] += (matchDescription.indexOf(searchTextNormalized) !== -1 || searchTextNormalized.indexOf(matchDescription) !== -1) ?  1 : 0;
                }
            }
            if (scores[group.id] > 0) {
                scores[group.id] = scores[group.id] / maxResult;
            }
        }
    }

    // Sorting the providers based on provider configuration.
    const sortedGroupsBasedOnConfig = _groupsConfig
        .map(({ id }) => id)
        .map(id => find(groups, g => g.id === id))
        .filter(group => group);

    // Ranking providers that have exact string matches to the top.
    const sortedGroups = sortedGroupsBasedOnConfig.slice(0).sort((group1, group2) => {
        const score1 = scores[group1.id];
        const score2 = scores[group2.id];

        return score2 - score1;
    });

    /**
     * Calculating the number of results to show.
     * For each provider, showing more results than the optimal number if
     * there is any provider that shows less than their optimal, which gives extra space.
     */
    return sortedGroups
      .map(group => {
        if (!group) {
          return null;
        }

        const extraCount = totalOptmalResults - totalResults;
        const maxCount = groupsConfigById[group.id].maxCount || Number.MAX_VALUE;
        const optimalCount = groupsConfigById[group.id].optimalCount || Number.MAX_VALUE;
        const suggestions = group.suggestions.slice(0, Math.min(group.suggestions.length, maxCount, optimalCount + extraCount));
        totalResults -= suggestions.length;
        return suggestions
          ? {
              ...group,
              suggestions
            }
          : null;
      }, [])
      .filter(group => group) as Array<IRankerSuggestionGroup>;
  }
